import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'
import {
  QuizQuestion,
  LearningQuizQuestionMatching,
  LearningQuizQuestionMultipleChoice,
  LearningQuizQuestionSequencing,
  LearningQuizQuestionSingleChoice,
  LearningQuizQuestionTrueFalse
} from './QuizQuestion.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import {
  type QuizVersionEntity,
  QuizVersionListSchema,
  QuizVersionSchema
} from '@/core/infrastructure/schemas/quiz/QuizVersionSchemas.ts'
import {
  isMatchingQuestion,
  isMultipleChoiceQuestion,
  isSequencingQuestion,
  isSingleChoiceQuestion,
  isTrueFalseQuestion
} from '@/core/infrastructure/schemas/quiz/QuizSchema.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'

/**
 * Représente une version d'un quiz d'apprentissage
 */
export class QuizVersion {
  public readonly id: number
  public readonly quizId: number
  public readonly versionNumber: number
  public readonly title: string | null
  public readonly comment: string | null
  public readonly status: LearningStatus.Values
  public questions: QuizQuestion[] // Pas readonly pour permettre les modifications

  constructor(data: QuizVersionEntity) {
    this.id = data.id
    this.quizId = data.quizId
    this.versionNumber = data.versionNumber
    this.title = data.title || null
    this.comment = data.comment || null
    this.status = data.status

    // Convertir les questions en leurs types spécifiques
    this.questions = data.questions.map((question) => {
      if (isSingleChoiceQuestion(question)) {
        return LearningQuizQuestionSingleChoice.create(question)
      } else if (isMultipleChoiceQuestion(question)) {
        return LearningQuizQuestionMultipleChoice.create(question)
      } else if (isTrueFalseQuestion(question)) {
        return LearningQuizQuestionTrueFalse.create(question)
      } else if (isMatchingQuestion(question)) {
        return LearningQuizQuestionMatching.create(question)
      } else if (isSequencingQuestion(question)) {
        return LearningQuizQuestionSequencing.create(question)
      } else {
        throw new Error(`Type de question non pris en charge`)
      }
    })

    this.questions.sort((a, b) => a.order - b.order)
  }

  /**
   * Crée une nouvelle instance de QuizVersion avec validation
   * @param data - Les données à valider et utiliser pour créer l'instance
   * @returns Une nouvelle instance validée de QuizVersion
   */
  static create(data: unknown): QuizVersion {
    return EntityValidator.validateAndCreate(QuizVersionSchema, data, QuizVersion, 'QuizVersion')
  }

  /**
   * Crée plusieurs instances de QuizVersion avec validation
   * @param data - Le tableau de données à valider
   * @returns Un tableau d'instances validées de QuizVersion
   */
  static createMany(data: unknown): QuizVersion[] {
    return EntityValidator.validateAndCreateMany(
      QuizVersionListSchema,
      data,
      QuizVersion,
      'QuizVersion'
    )
  }

  /**
   * Vérifie si cette version est active
   */
  get isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  /**
   * Vérifie si cette version est un brouillon
   */
  get isDraft(): boolean {
    return this.status === 'DRAFT'
  }

  /**
   * Vérifie si cette version est archivée
   */
  get isArchived(): boolean {
    return this.status === 'ARCHIVED'
  }

  /**
   * Retourne le titre de la version ou un titre par défaut
   */
  get displayTitle(): string {
    return this.title || `Version ${this.versionNumber}`
  }

  /**
   * Retourne le nombre de questions dans cette version
   */
  get questionCount(): number {
    return this.questions.length
  }

  /**
   * Vérifie si la version a des questions
   */
  get hasQuestions(): boolean {
    return this.questions.length > 0
  }

  /**
   * Vérifie si la version autorise les modifications structurelles
   * Une version active ne peut modifier que les libellés, pas la structure
   */
  get canModifyStructure(): boolean {
    return this.isDraft
  }

  /**
   * Vérifie si la version autorise les modifications de contenu (libellés)
   */
  get canModifyContent(): boolean {
    return this.isDraft || this.isActive
  }

  /**
   * Vérifie si la version autorise la réorganisation des questions
   * La réorganisation est autorisée même sur les versions actives
   */
  get canReorderQuestions(): boolean {
    return this.isDraft || this.isActive
  }

  /**
   * Ajoute une nouvelle question à la version
   * @param type - Le type de question à créer
   * @throws Error si la version ne permet pas les modifications structurelles
   */
  addNewQuestion(
    type: LearningQuizQuestionType.Values = LearningQuizQuestionType.Values.SINGLE_CHOICE
  ): QuizQuestion {
    if (!this.canModifyStructure) {
      throw new Error(
        "Impossible d'ajouter une question à une version active. Créez une nouvelle version brouillon."
      )
    }

    let newQuestion: QuizQuestion
    const newOrder = this.questions.length + 1

    switch (type) {
      case LearningQuizQuestionType.Values.SINGLE_CHOICE:
        newQuestion = LearningQuizQuestionSingleChoice.createEmpty()
        break
      case LearningQuizQuestionType.Values.MULTIPLE_CHOICE:
        newQuestion = LearningQuizQuestionMultipleChoice.createEmpty()
        break
      case LearningQuizQuestionType.Values.TRUE_FALSE:
        newQuestion = LearningQuizQuestionTrueFalse.createEmpty()
        break
      case LearningQuizQuestionType.Values.MATCHING:
        newQuestion = LearningQuizQuestionMatching.createEmpty()
        break
      case LearningQuizQuestionType.Values.SEQUENCING:
        newQuestion = LearningQuizQuestionSequencing.createEmpty()
        break
      default:
        newQuestion = LearningQuizQuestionSingleChoice.createEmpty()
    }

    newQuestion.setOrder(newOrder)
    this.questions.push(newQuestion)

    return newQuestion
  }

  /**
   * Duplique une question existante
   * @param questionToDuplicate - La question à dupliquer
   * @throws Error si la version ne permet pas les modifications structurelles
   * @returns QuizQuestion - La question dupliquée
   */
  duplicateQuestion(questionToDuplicate: QuizQuestion): QuizQuestion {
    if (!this.canModifyStructure) {
      throw new Error(
        'Impossible de dupliquer une question dans une version active. Créez une nouvelle version brouillon.'
      )
    }

    const clonedQuestion = questionToDuplicate.clone()
    clonedQuestion.id = 0
    clonedQuestion.setOrder(this.questions.length + 1)
    clonedQuestion.label = `${clonedQuestion.label} (copie)`

    this.questions.push(clonedQuestion)
    return clonedQuestion
  }

  /**
   * Supprime une question par son index
   * @param index - L'index de la question à supprimer
   * @throws Error si la version ne permet pas les modifications structurelles
   */
  removeQuestion(index: number): void {
    if (!this.canModifyStructure) {
      throw new Error(
        "Impossible de supprimer une question d'une version active. Créez une nouvelle version brouillon."
      )
    }

    if (index < 0 || index >= this.questions.length) {
      throw new Error('Index de question invalide')
    }

    this.questions.splice(index, 1)
    this.updateQuestionsOrder()
  }

  /**
   * Met à jour l'ordre des questions après une modification
   */
  updateQuestionsOrder(): void {
    if (!this.canReorderQuestions) {
      throw new Error("Impossible de réorganiser les questions d'une version archivée.")
    }

    this.questions.forEach((question, index) => {
      question.setOrder(index + 1)
    })
  }

  /**
   * Modifie le libellé d'une question (autorisé pour les versions actives)
   * @param questionIndex - L'index de la question
   * @param newLabel - Le nouveau libellé
   * @throws Error si la version ne permet pas les modifications de contenu
   */
  updateQuestionLabel(questionIndex: number, newLabel: string): void {
    if (!this.canModifyContent) {
      throw new Error('Impossible de modifier une question dans une version archivée.')
    }

    if (questionIndex < 0 || questionIndex >= this.questions.length) {
      throw new Error('Index de question invalide')
    }

    this.questions[questionIndex].label = newLabel
  }

  /**
   * Modifie les feedbacks d'une question (autorisé pour les versions actives)
   * @param questionIndex - L'index de la question
   * @param feedbacks - Les nouveaux feedbacks
   * @throws Error si la version ne permet pas les modifications de contenu
   */
  updateQuestionFeedbacks(
    questionIndex: number,
    feedbacks: { correct: string; wrong: string }
  ): void {
    if (!this.canModifyContent) {
      throw new Error('Impossible de modifier une question dans une version archivée.')
    }

    if (questionIndex < 0 || questionIndex >= this.questions.length) {
      throw new Error('Index de question invalide')
    }

    this.questions[questionIndex].feedbacks = feedbacks
  }

  /**
   * Retourne le payload formaté pour la mise à jour via l'API
   * @returns Le payload de mise à jour avec tous les champs nécessaires
   */
  toUpdatePayload() {
    return {
      id: this.id,
      quizId: this.quizId,
      title: this.title,
      comment: this.comment,
      questions: this.questions.map((question) => ({
        id: question.id,
        type: question.type,
        label: question.label,
        order: question.order,
        answers: question.answers,
        feedbacks: question.feedbacks
      }))
    }
  }

  /**
   * Trie les versions par numéro de version (ordre décroissant)
   */
  static compareByVersionNumber(a: QuizVersion, b: QuizVersion): number {
    return a.versionNumber - b.versionNumber
  }

  /**
   * Trouve la version active parmi une liste de versions
   */
  static findActiveVersion(versions: QuizVersion[]): QuizVersion | null {
    return versions.find((version) => version.isActive) || null
  }

  /**
   * Trouve la version la plus récente parmi une liste de versions
   */
  static findLatestVersion(versions: QuizVersion[]): QuizVersion | null {
    if (versions.length === 0) return null

    return versions.reduce((latest, current) =>
      current.versionNumber > latest.versionNumber ? current : latest
    )
  }

  /**
   * Obtient toutes les versions triées par numéro de version (décroissant)
   */
  static sortByVersionDescending(versions: QuizVersion[]): QuizVersion[] {
    return [...versions].sort((a, b) => b.versionNumber - a.versionNumber)
  }
}
